import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import type { Identifier, XYCoord } from 'dnd-core';
import { useRef } from 'react';
import { useDrag, useDrop, ConnectDragSource } from 'react-dnd';
import update from 'immutability-helper';

const ItemTypes = {
  CARD: 'card',
};

type SortableItemProps = {
  item: ItemType;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  renderItem: (item: ItemType, index?: number, dragHandleRef?: ConnectDragSource) => JSX.Element;
  onDrop?: () => void;
  useDragHandle?: boolean;
};

type DragItem = {
  index: number;
  id: string;
  type: string;
};

export const SortableItem = ({
  item,
  index,
  moveItem,
  renderItem,
  onDrop,
  useDragHandle,
}: SortableItemProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    drop() {
      // item: DragItem, monitor
      onDrop && onDrop();
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id: item.id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  if (useDragHandle) {
    drop(ref);
    preview(ref);
  } else {
    drag(drop(ref));
  }

  const style: React.CSSProperties = {
    transition: 'transform 200ms ease, box-shadow 200ms ease, opacity 200ms ease',
    userSelect: 'none',
    ...(isDragging
      ? {
          opacity: 0.9,
          transform: 'scale(1.03)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          zIndex: 50,
          position: 'relative',
          borderRadius: '8px',
        }
      : { opacity: 1 }),
  };

  return (
    <div ref={ref} style={style} data-handler-id={handlerId}>
      {useDragHandle ? renderItem(item, index, drag) : renderItem(item, index)}
    </div>
  );
};

export interface ContainerState {
  cards: unknown[];
}

export interface ItemType {
  id: string;
}

export type { ConnectDragSource };

export type DndProps = {
  items: ItemType[];
  setItems: (items: ItemType[]) => void;
  renderContainer: (items: React.ReactNode) => JSX.Element;
  renderItem: (item: ItemType, index?: number, dragHandleRef?: ConnectDragSource) => JSX.Element;
  onDrop?: () => void;
  useDragHandle?: boolean;
};

export const Container = ({
  items,
  setItems,
  renderContainer,
  ...rest
}: DndProps) => {
  const moveItem = (dragIndex: number, hoverIndex: number) => {
    const newItems = update(items, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, items[dragIndex]],
      ],
    });
    setItems(newItems);
  };

  const renderedItems = items.map((item, i) => (
    <SortableItem
      key={item.id}
      index={i}
      item={item}
      moveItem={moveItem}
      {...rest}
    />
  ));

  return <>{renderContainer(renderedItems)}</>;
};

export const Dnd = (props: DndProps) => {
  return (
    <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
      <Container {...props} />
    </DndProvider>
  );
};
